import React, { useState } from 'react';
import PageHeaderText from 'components/base/page-header-text';
import useDashboardContext from 'contexts/dashboard.context';
import PageText from 'components/base/page-text';
import { Button } from '@cokitchen/cokitchen-components';
import { handleApiError } from 'utils/functions';
import { SuccessModal } from 'components/base/success-modal';
import { ROUTE_KEYS, ROUTE_PREFIX } from 'utils/constants';
import { apiService } from 'services/base.service';
import useCokitchenContext from 'contexts/cokitchen.context';
import { ENUM_COKITCHEN_TYPE } from 'utils/enum';

const CreateCokitchenExpore = () => {
  const { cokitchens, cokitchenLoading } = useDashboardContext();
  const { newCokitchen } = useCokitchenContext();
  const [selectedCokitchenIds, setSelectedCokitchenIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const cokitchenOptions = cokitchens.filter(
    (c) => c.cokitchen_type === ENUM_COKITCHEN_TYPE.STANDARD
  );

  const toggleCokitchen = (id: string) => {
    if (selectedCokitchenIds.includes(id))
      setSelectedCokitchenIds((c) => c.filter((c) => c !== id));
    else setSelectedCokitchenIds((c) => [...c, id]);
  };

  const fetchExplorePosts = async (cokitchen_id: string) => {
    try {
      const res = await apiService.get('internal/marketing/explore-posts', {
        isInternal: true,
        params: {
          cokitchen_id
        }
      });
      return res.data.data.map((post: { id: string }) => post.id);
    } catch (error) {
      handleApiError(error);
    }
  };

  const createCokitchenExplore = async () => {
    if (!newCokitchen?.id) return;
    try {
      setLoading(true);
      const url =
        newCokitchen.cokitchen_type === ENUM_COKITCHEN_TYPE.STANDARD
          ? 'marketing/cokitchen'
          : 'marketing/pop/cokitchen';
      const explorePosts = await Promise.all(
        selectedCokitchenIds.map((id) => fetchExplorePosts(id))
      );
      await apiService.put(url, {
        data: { cokitchen_id: newCokitchen.id, explore_posts_ids: explorePosts.flat() }
      });
      setShowSuccessModal(true);
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='flex flex-col gap-4'>
      <PageHeaderText
        title='Explore Settings'
        description='Select cokitchen to get explore posts from'
      />
      {cokitchenLoading ? (
        <PageText text='Loading...' />
      ) : cokitchenOptions.length === 0 ? (
        <PageText text='No cokitchens found' />
      ) : (
        <ul className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5'>
          {cokitchenOptions.map((cokitchen) => (
            <li
              key={cokitchen.id}
              className={`flex items-center gap-3 rounded-lg border border-gray-11 overflow-hidden cursor-pointer select-none ${
                selectedCokitchenIds.includes(cokitchen.id)
                  ? 'bg-red-1 border-red-5 text-red-5'
                  : ''
              }`}
              onClick={() => toggleCokitchen(cokitchen.id)}>
              {cokitchen.images[0] || cokitchen.event_image ? (
                <img
                  src={cokitchen.images[0] || cokitchen.event_image || ''}
                  alt={`${cokitchen.name} logo`}
                  className='h-16 aspect-square object-cover'
                />
              ) : (
                <div className='h-16 aspect-square bg-red-2'></div>
              )}
              <p className='font-medium'>{cokitchen.name}</p>
            </li>
          ))}
        </ul>
      )}

      <div className='self-end flex items-center gap-2 mt-8'>
        <Button
          variant='link'
          color='primary'
          onClick={() => setSelectedCokitchenIds([])}
          disabled={loading || !selectedCokitchenIds.length}
          className='w-[157px] h-[40px] font-medium text-sm'>
          Undo Changes
        </Button>
        <Button
          variant='block'
          color='primary'
          onClick={createCokitchenExplore}
          disabled={!selectedCokitchenIds.length}
          loading={loading}
          className='w-[157px] h-[40px] font-medium text-sm'>
          Continue
        </Button>
      </div>
      <SuccessModal
        onClose={() => setShowSuccessModal(false)}
        show={showSuccessModal}
        link={`${ROUTE_PREFIX.FOOD}/${ROUTE_KEYS.OVERVIEW}`}
        linkText='BACK TO HOME'>
        You&apos;re all set!
      </SuccessModal>
    </div>
  );
};

export default CreateCokitchenExpore;
